/**
 * API Service
 * Handles all HTTP requests to the backend
 */

import { API_URL } from '../config.js';
import { storage } from '../utils/storage.js';
import { firebaseAuth } from './firebase.js';

/**
 * API Client with automatic token handling
 */
class ApiClient {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }

    /**
     * Get authorization headers
     */
    async getHeaders() {
        const headers = {
            'Content-Type': 'application/json'
        };

        // Get JWT token from storage
        const token = storage.getToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    }

    /**
     * Make HTTP request
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const headers = await this.getHeaders();

        const config = {
            ...options,
            headers: {
                ...headers,
                ...options.headers
            }
        };

        try {
            const response = await fetch(url, config);

            // Handle 401 Unauthorized - token expired
            if (response.status === 401) {
                storage.clearAll();
                window.router.navigate('/auth');
                throw new Error('Sesión expirada. Por favor, inicia sesión de nuevo.');
            }

            // Parse response
            const data = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(data?.message || `Error ${response.status}`);
            }

            return data;
        } catch (error) {
            // Network error
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                throw new Error('Error de conexión. Verifica que el servidor esté funcionando.');
            }
            throw error;
        }
    }

    // HTTP Methods
    get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }

    post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }
}

// Create API client instance
const apiClient = new ApiClient(API_URL);

/**
 * Auth API
 */
export const authApi = {
    /**
     * Login with Firebase token
     */
    async loginWithFirebase(firebaseToken) {
        const response = await apiClient.post('/auth/login', { firebaseToken });

        // Store the JWT token
        if (response.accessToken) {
            storage.setToken(response.accessToken);
        }

        return response;
    },

    /**
     * Get current user info
     */
    async getCurrentUser() {
        return apiClient.get('/auth/me');
    },

    /**
     * Logout (clear local data)
     */
    async logout() {
        await firebaseAuth.logout();
        storage.clearAll();
    }
};

/**
 * Profile API
 */
export const profileApi = {
    /**
     * Get user profile
     */
    async getProfile() {
        return apiClient.get('/profile');
    },

    /**
     * Create or update profile
     */
    async saveProfile(profileData) {
        return apiClient.post('/profile', profileData);
    },

    /**
     * Update profile
     */
    async updateProfile(profileData) {
        return apiClient.patch('/profile', profileData);
    }
};



/**
 * Guide API
 */
export const guideApi = {
    /**
     * Get guide progress
     */
    async getProgress() {
        return apiClient.get('/guide');
    },

    /**
     * Update guide progress
     */
    async updateProgress(step, stepsData) {
        return apiClient.patch('/guide', { step, stepsData });
    },

    /**
     * Mark guide as completed
     */
    async complete() {
        return apiClient.post('/guide/complete');
    }
};

/**
 * Expenses API
 */
export const expensesApi = {
    /**
     * Get expenses
     */
    async getExpenses() {
        return apiClient.get('/expenses');
    },

    /**
     * Save expenses
     */
    async saveExpenses(expensesData) {
        return apiClient.post('/expenses', expensesData);
    },

    /**
     * Update expenses
     */
    async updateExpenses(expensesData) {
        return apiClient.patch('/expenses', expensesData);
    }
};

/**
 * Subscriptions API
 */
export const subscriptionApi = {
    /**
     * Create Stripe checkout session
     */
    async createCheckout(plan) {
        return apiClient.post('/subscriptions/checkout', { plan });
    },

    /**
     * Get subscription status
     */
    async getStatus() {
        return apiClient.get('/subscriptions/status');
    },

    /**
     * Cancel subscription
     */
    async cancel() {
        return apiClient.post('/subscriptions/cancel');
    }
};

export default apiClient;
